import { evaluateExpression as evaluateExpressionUtil } from "../../../../utils/evaluateExpression";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ModalAtom from "../../../../components/atoms/modal/modal";
import TextFieldAtom from "../../../../components/atoms/text-field/text-field";
import {
	applyAufmassResultRaw,
	markDirtyAfterMutation,
	selectEditGroupDataV2,
	selectFormDataV2,
	selectPositionsV2,
	selectSelectedPositionV2,
	selectShowAddGroupModalV2,
	setAddGroupModal,
	setAufmassError,
} from "../../../../store/slices/aufmassV2Slice";
import { selectCurrentProjectId } from "../../../../store/slices/projectDetailsSlice";
import {
	useAddAufmassGroupMutation,
	useUpdateAufmassGroupMutation,
} from "../../../../store/services/aufmass";
import { showToast } from "../../../../utils/method";
import LineEditorV2 from "./local/LineEditorV2";
import QuantityDisplayFooterV2 from "./local/QuantityDisplayFooterV2";

const checkQuantityMismatch = (manualQuantity, calculatedQuantity) => {
	if (!manualQuantity || !calculatedQuantity) return false;
	const manual = parseFloat(manualQuantity);
	const calculated = parseFloat(calculatedQuantity);
	return Math.abs(manual - calculated) > 0.001;
};

const calculateGroupTotal = (lines) =>
	lines.reduce((sum, line) => {
		const quantity = parseFloat(line.quantity) || 0;
		return sum + quantity;
	}, 0);

function AddGroupModalV2() {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	// Use V2 selectors
	const showAddGroupModal = useSelector(selectShowAddGroupModalV2);
	const selectedPosition = useSelector(selectSelectedPositionV2);
	const editGroupData = useSelector(selectEditGroupDataV2);
	const positions = useSelector(selectPositionsV2);
	const formData = useSelector(selectFormDataV2);
	const projectId = useSelector(selectCurrentProjectId);
	const [addAufmassGroup] = useAddAufmassGroupMutation();
	const [updateAufmassGroup] = useUpdateAufmassGroupMutation();

	// Separate form state and errors into two distinct state variables
	const [formState, setFormState] = useState({
		groupName: "",
		groupLines: [],
		isSubmitting: false,
	});

	// Fix: Ensure proper error structure and access pattern
	const [errors, setErrors] = useState({
		groupName: "",
		lines: {}, // Object to store errors by line index
	});

	// Add ref to hold first line text input reference
	const firstLineTextRef = useRef(null);
	const [focusLineIndex, setFocusLineIndex] = useState(null);
	const [modalError, setModalError] = useState("");

	// Effect to initialize the modal when opened
	useEffect(() => {
		if (showAddGroupModal) {
			if (editGroupData && editGroupData.isEditMode) {
				// Initialize form with edit data
				setFormState((prev) => ({
					...prev,
					groupName: editGroupData.groupName || "",
					groupLines: editGroupData.lines?.map((line, index) => ({
						id: line.id || Date.now() + index,
						text: line.text || "",
						comments: line.comments || "",
						factorOperation: line.factorOperation || "",
						manualQuantity: line.manualQuantity?.toString() || "",
						quantity: line.quantity?.toString() || "",
						quantityMismatch: false,
					})) || [
							{
								id: Date.now(),
								text: "",
								comments: "",
								factorOperation: "",
								manualQuantity: "",
								quantity: "",
								quantityMismatch: false,
							},
						],
				}));
			} else {
				// Initialize for new group
				const newLineId = Date.now();
				const positionNumber =
					selectedPosition &&
						positions[selectedPosition.id]?.itemNumber
						? positions[selectedPosition.id].itemNumber
						: "";
				setFormState((prev) => ({
					...prev,
					groupName: "",
					groupLines: [
						{
							id: newLineId,
							text: positionNumber,
							comments: "",
							factorOperation: "",
							manualQuantity: "",
							quantity: "",
							quantityMismatch: false,
						},
					],
				}));
			}

			setErrors({
				groupName: "",
				lines: [
					{
						text: "",
						factorOperation: "",
						manualQuantity: "",
					},
				],
			});

			// Focus on the first line text input after a short delay
			setTimeout(() => {
				if (firstLineTextRef.current) {
					firstLineTextRef.current.focus();
				}
			}, 100);
		}
	}, [showAddGroupModal, editGroupData, positions, selectedPosition]);

	useEffect(() => {
		if (!showAddGroupModal) {
			setFormState({
				groupName: "",
				groupLines: [],
				isSubmitting: false,
			});
			setErrors({
				groupName: "",
				lines: {},
			});
		}
	}, [showAddGroupModal]);

	// Update group name
	const handleGroupNameChange = (e) => {
		const value = e.target.value;
		setFormState((prev) => ({
			...prev,
			groupName: value,
		}));

		// Clear error when user types
		if (errors.groupName) {
			setErrors((prev) => ({
				...prev,
				groupName: "",
			}));
		}
	};

	// Complete handleLineChange function
	const handleLineChange = (index, field, value) => {
		// Update form state
		setFormState((prev) => {
			const newLines = [...prev.groupLines];
			newLines[index] = { ...newLines[index], [field]: value };

			if (field === "factorOperation") {
				if (value.trim() === "") {
					// Empty formula field - clear both quantities and flags
					newLines[index].quantity = "";
					newLines[index].manualQuantity = "";
					newLines[index].quantityMismatch = false;

					// Clear factorOperation error if it exists
					if (getLineError(index, "factorOperation")) {
						setLineError(index, "factorOperation", "");
					}
				} else {
					// Non-empty formula - try to evaluate
					const result = evaluateExpressionUtil(value);

					if (result !== null) {
						// Valid formula - store the raw calculated value
						newLines[index].quantity = result;
						newLines[index].manualQuantity = result;
						newLines[index].quantityMismatch = false;

						// Clear factorOperation error if it exists
						if (getLineError(index, "factorOperation")) {
							setLineError(index, "factorOperation", "");
						}
					} else {
						// Invalid formula - clear both quantity and manualQuantity
						newLines[index].quantity = "";
						newLines[index].manualQuantity = "";
						newLines[index].quantityMismatch = false;

						// Set factorOperation error
						setLineError(
							index,
							"factorOperation",
							t("error-invalid-formula"),
						);
					}
				}
			} else if (field === "manualQuantity") {
				// User is directly changing the manualQuantity field
				if (value.trim() === "") {
					// Empty manual quantity
					newLines[index].quantityMismatch = false;
				} else {
					// Check if the manual entry matches the calculated value
					newLines[index].quantityMismatch =
						checkQuantityMismatch(
							value,
							newLines[index].quantity,
						);
				}
			}

			// Clear field error when user types (except for factorOperation which has special handling above)
			if (field !== "factorOperation" && getLineError(index, field)) {
				setLineError(index, field, "");
			}

			return {
				...prev,
				groupLines: newLines,
			};
		});
	};

	const handleAddLine = () => {
		const newLineId = Date.now() + Math.random();
		const positionNumber =
			selectedPosition && positions[selectedPosition.id]?.itemNumber
				? positions[selectedPosition.id].itemNumber
				: "";
		const newLine = {
			id: newLineId,
			text: positionNumber,
			comments: "",
			factorOperation: "",
			manualQuantity: "",
			quantity: "",
			quantityMismatch: false,
		};

		// Update form state to add new line
		setFormState((prev) => ({
			...prev,
			groupLines: [...prev.groupLines, newLine],
		}));

		// Initialize empty error object for the new line
		const newLineIndex = formState.groupLines.length;
		setErrors((prev) => ({
			...prev,
			lines: {
				...prev.lines,
				[newLineIndex]: {
					text: "",
					factorOperation: "",
					manualQuantity: "",
				},
			},
		}));

		// Set focus to the new line
		setFocusLineIndex(newLineIndex);
	};

	// Effect to focus on the specified line
	useEffect(() => {
		if (focusLineIndex !== null) {
			// Small delay to ensure the DOM has updated
			setTimeout(() => {
				const textField = document.getElementById(
					`line-text-${formState.groupLines[focusLineIndex]?.id}`,
				);
				if (textField) {
					textField.focus();
				}
				setFocusLineIndex(null); // Reset focus index
			}, 100);
		}
	}, [focusLineIndex, formState.groupLines]);

	const handleRemoveLine = (index) => {
		// Remove line from form state
		setFormState((prev) => ({
			...prev,
			groupLines: prev.groupLines.filter((_, i) => i !== index),
		}));

		// Clear errors for this line
		clearLineErrors(index);

		// Update focus if needed
		if (focusLineIndex === index) {
			setFocusLineIndex(null);
		}
	};

	const handleMoveLine = (index, direction) => {
		const newIndex = direction === "up" ? index - 1 : index + 1;

		if (newIndex < 0 || newIndex >= formState.groupLines.length) {
			return;
		}

		setFormState((prev) => {
			const newLines = [...prev.groupLines];
			[newLines[index], newLines[newIndex]] = [
				newLines[newIndex],
				newLines[index],
			];
			return {
				...prev,
				groupLines: newLines,
			};
		});
	};

	const validateForm = () => {
		let isValid = true;
		const newErrors = {
			groupName: "",
			lines: { ...errors.lines },
		};

		// Clear any previous modal error
		setModalError("");

		if (formState.groupLines.length === 0) {
			isValid = false;
			newErrors.groupName = t("aufmass.groups.error.no-lines");
		}

		formState.groupLines.forEach((line, index) => {
			const lineErrors = {
				text: "",
				factorOperation: "",
				manualQuantity: "",
			};

			if (!line.text.trim()) {
				lineErrors.text = t("error-required");
				isValid = false;
			}

			if (!line.factorOperation.trim()) {
				lineErrors.factorOperation = t("error-required");
				isValid = false;
			}
			if (line.quantity === "") {
				lineErrors.factorOperation = t("error-invalid-formula");
				isValid = false;
			}

			if (
				line.manualQuantity === null ||
				line.manualQuantity === undefined ||
				line.manualQuantity === ""
			) {
				lineErrors.manualQuantity = t("error-required");
				isValid = false;
			}

			if (index >= newErrors.lines.length) {
				newErrors.lines[index] = lineErrors;
			} else {
				newErrors.lines[index] = {
					...newErrors.lines[index],
					...lineErrors,
				};
			}
		});

		setErrors(newErrors);
		return isValid;
	};

	// Helper function to parse raw quantity values
	const parseRawQuantity = (value) => {
		if (typeof value === "number") return value;
		if (!value) return 0;

		// Convert formatted string to number
		if (typeof value === "string") {
			return parseFloat(value.replace(/\./g, "").replace(",", ".")) || 0;
		}

		return 0;
	};

	// Helper function to process line data for submission
	const processLineForSubmission = (line, index) => {
		// Ensure we use numeric values, not formatted strings
		const rawQuantity =
			line.quantity !== ""
				? line.quantity // If quantity is already calculated, use it
				: parseRawQuantity(line.manualQuantity); // Otherwise parse from manualQuantity

		return {
			text: line.text,
			comments: line.comments || "",
			factorOperation: line.factorOperation,
			manualQuantity: line.manualQuantity,
			sequence: index,
			quantity: rawQuantity,
			positionId: selectedPosition?.id,
		};
	};

	const handleSubmit = () => {
		if (!validateForm()) {
			return;
		}

		// Process lines data
		const processedLines = formState.groupLines.map(
			processLineForSubmission,
		);

		// Create group object based on whether we're editing or creating
		const groupData = {
			groupName: formState.groupName,
			positionId: selectedPosition?.id,
			lines: processedLines,
			// Include edit mode information
			isEditMode: editGroupData?.isEditMode || false,
			groupId: editGroupData?.groupId || null,
		};

		// Submit the group data
		handleAddGroupSubmit(groupData);

		// Reset form
		setFormState({
			groupName: "",
			groupLines: [],
			isSubmitting: false,
		});
	};

	const groupTotal = useMemo(
		() => calculateGroupTotal(formState.groupLines),
		[formState.groupLines],
	);

	const handleAddGroupModalClose = useCallback(() => {
		dispatch(setAddGroupModal({ show: false, position: null }));
	}, [dispatch]);

	const handleAddGroupSubmit = useCallback(
		async (group) => {
			if (selectedPosition) {
				try {
					// Get the invoice position ID from positions lookup (using LV position ID as key)
					const invoicePositionId =
						positions[selectedPosition.id]?.invoicePositionId;

					if (!invoicePositionId) {
						throw new Error(
							"Invoice position ID not found for the selected position",
						);
					}

					if (group.isEditMode && group.groupId) {
						// Edit mode - update existing group
						const response = await updateAufmassGroup({
							projectId,
							aufmassId: formData.id,
							groupId: group.groupId,
							body: { group },
						}).unwrap();
						dispatch(applyAufmassResultRaw(response));
						dispatch(markDirtyAfterMutation());

						dispatch(
							setAddGroupModal({ show: false, position: null }),
						);
						showToast(t("aufmass.group-updated-successfully"), {
							type: "success",
						});
					} else {
						// Add mode - create new group
						const response = await addAufmassGroup({
							projectId,
							aufmassId: formData.id,
							body: { group, positionId: invoicePositionId },
						}).unwrap();
						dispatch(applyAufmassResultRaw(response));
						dispatch(markDirtyAfterMutation());

						dispatch(
							setAddGroupModal({ show: false, position: null }),
						);
						showToast(t("aufmass.group-added-successfully"), {
							type: "success",
						});
					}
				} catch (error) {
					dispatch(setAufmassError(error?.data?.message || error?.message));
					const errorMessage = group.isEditMode
						? t("aufmass.group-update-failed")
						: t("aufmass.group-add-failed");

					showToast(error?.data?.message || error?.message || errorMessage, {
						type: "error",
					});
				}
			}
		},
		[
			dispatch,
			formData.id,
			projectId,
			addAufmassGroup,
			updateAufmassGroup,
			selectedPosition,
			positions,
			t,
		],
	);

	// Helper function to safely get line error
	const getLineError = (lineIndex, fieldName) => {
		return errors.lines[lineIndex]?.[fieldName] || "";
	};

	// Helper function to set line error
	const setLineError = (lineIndex, fieldName, error) => {
		setErrors((prev) => ({
			...prev,
			lines: {
				...prev.lines,
				[lineIndex]: {
					...prev.lines[lineIndex],
					[fieldName]: error,
				},
			},
		}));
	};

	// Clear errors for a specific line
	const clearLineErrors = (lineIndex) => {
		setErrors((prev) => {
			const newLines = { ...prev.lines };
			delete newLines[lineIndex];
			return {
				...prev,
				lines: newLines,
			};
		});
	};

	// Calculate the position total
	const positionTotal = useMemo(() => {
		if (editGroupData?.isEditMode) {
			// In edit mode, calculate preview: existing total minus old group plus new group
			const existingTotal =
				positions[selectedPosition?.id]?.aufmass?.quantity || 0;
			// Calculate the original group total from the original lines
			const originalGroupTotal = editGroupData?.lines
				? calculateGroupTotal(editGroupData.lines)
				: 0;
			return existingTotal - originalGroupTotal + groupTotal;
		} else {
			// In add mode, add the current group total to existing aufmass
			const existingTotal =
				positions[selectedPosition?.id]?.aufmass?.quantity || 0;
			return existingTotal + groupTotal;
		}
	}, [
		editGroupData?.isEditMode,
		editGroupData?.lines,
		positions,
		selectedPosition?.id,
		groupTotal,
	]);

	const modalBody = (
		<Form>
			{/* Display validation error at the top of the form */}
			{modalError && (
				<div className="alert alert-danger mb-3">{modalError}</div>
			)}

			<Row className="mb-4">
				<Col md={12}>
					<TextFieldAtom
						label={t("aufmass.groups.add")}
						value={formState.groupName}
						onChange={handleGroupNameChange}
						error={errors.groupName}
						placeholder={t("aufmass.groups.group")}
					/>
				</Col>
			</Row>

			<div className="d-flex justify-content-between align-items-center mb-3 sticky-top bg-white py-2">
				<h5>{t("aufmass.add-line")}</h5>
				<Button
					variant="outline-primary"
					size="sm"
					onClick={handleAddLine}
				>
					<FontAwesomeIcon icon={faPlus} className="me-1" />
					{t("aufmass.add-line")}
				</Button>
			</div>

			<div className="group-lines-container">
				{formState.groupLines.length === 0 ? (
					<div className="text-center text-muted py-4 border rounded">
						{t("aufmass.no-lines-found")}
					</div>
				) : (
					formState.groupLines.map((line, index) => (
						<div
							key={line.id}
							className="group-line-item border rounded p-3 mb-3"
						>
							<LineEditorV2
								line={line}
								index={index}
								onLineChange={handleLineChange}
								getLineError={getLineError}
								showSequenceControls={true}
								onMoveUp={() => handleMoveLine(index, "up")}
								onMoveDown={() => handleMoveLine(index, "down")}
								onRemove={() => handleRemoveLine(index)}
								canMoveUp={index > 0}
								canMoveDown={
									index < formState.groupLines.length - 1
								}
								autoFocus={index === focusLineIndex}
								lineRef={index === 0 ? firstLineTextRef : null}
							/>
						</div>
					))
				)}
			</div>
		</Form>
	);

	return (
		<ModalAtom
			show={showAddGroupModal}
			handleClose={handleAddGroupModalClose}
			title={`${editGroupData?.isEditMode
					? t("aufmass.groups.edit")
					: t("aufmass.groups.add")
				}${selectedPosition?.itemNumber
					? ` - ${t("position")} ${selectedPosition.itemNumber}`
					: ""
				}`}
			handleSubmit={handleSubmit}
			size="xl"
			footer={
				<QuantityDisplayFooterV2
					key={`${selectedPosition?.id}-${groupTotal}-${positionTotal}`}
					selectedPosition={selectedPosition}
					totalQuantity={positionTotal}
					itemQuantity={groupTotal}
					itemLabel={t("aufmass.groups.subtotal")}
					unitTag={selectedPosition?.unit || ""}
					positionsData={positions}
				/>
			}
			body={modalBody}
			customClassName="add-group-modal"
		/>
	);
}

export default AddGroupModalV2;
